import { animation, css } from 'twind/css'

import { bounceAnimation, rippleAnimation } from '@core/styles/animations.twind'
import type { ButtonBorderStyle } from '@core/types'

export const borderRadiusSettings: Record<ButtonBorderStyle, number> = {
  default: 4,
  rounded: 30,
  hard: 0,
}

export const heightSettings = {
  small: 25,
  default: 36,
}

export type ButtonSize = 'small' | 'default'
export type ButtonVariant = 'primary' | 'outlined'
export interface BaseButtonProps {
  borderStyle?: ButtonBorderStyle
  disabled?: boolean
  ripple?: boolean
  variant?: ButtonVariant
  size?: ButtonSize
  icon?: React.ReactNode
  block?: boolean
  isLoading?: boolean
}

export const rippleAnimationSetting = animation(
  '2s infinite cubic-bezier(0.65, 0, 0.34, 1)',
  rippleAnimation,
)

export const rippleRings = css({
  opacity: '0',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center',
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  content: '',
  height: '100%',
  width: '100%',
  background: 'var(--button-color, var(--gradient))',
  zIndex: -1,
})

export const bounceAnimationSetting = animation('0.3s 1', bounceAnimation)

export const primaryButtonStyle = css({
  background: 'var(--button-color, var(--gradient))',
  border: 'none',
  color: 'var(--button-font-color, var(--white)) !important',

  // Need a higher specificity to override the global button styles.
  '> svg, > span': {
    color: 'inherit !important',
  },
})

export const outlinedButtonStyle = css({
  borderColor: 'currentColor',
  borderWidth: '1px',
  borderStyle: 'solid',
  background: 'transparent',
})

export const smallButtonStyle = css({
  fontSize: '12px',
  height: `${heightSettings.small}px`,
  lineHeight: '18px',
  padding: '2px 22px',
})

/**
 * Return the styles for the button.
 */
export const button = css`
  align-items: center;
  cursor: pointer;
  display: flex;
  font-family: inherit;
  font-size: 16px;
  font-weight: 600;
  gap: 2px;
  height: ${heightSettings.default}px;
  justify-content: center;
  line-height: 22px;
  max-width: 100%;
  padding: 7px 22px;
  position: relative;
  text-decoration: none;
  user-select: none;
`

export const unstyledButton = css({
  background: 'none',
  fontSize: 'inherit',
  fontWeight: 'inherit',
  textAlign: 'inherit',
  color: 'inherit',
})
